<!-- src/app/timesheets/components/weekly-timesheet.component.html -->
<div class="weekly-timesheet-wrapper">
  <div
    class="toast show"
    *ngIf="toastMessage"
    [class.success]="toastKind === 'success'"
    [class.error]="toastKind === 'error'"
    [class.warning]="toastKind === 'info'"
    (click)="clearToast()"
    role="status"
    aria-live="polite"
  >
    <img
      class="icon"
      [src]="
        toastKind === 'success'
          ? '/success.png'
          : toastKind === 'error'
            ? '/fail.png'
            : '/warning.png'
      "
      alt=""
      aria-hidden="true"
    />

    <div class="toast-title">
      {{ toastKind === 'success' ? 'Success' : toastKind === 'error' ? 'Action needed' : 'Notice' }}
    </div>

    <div class="toast-body">{{ toastMessage }}</div>
  </div>

  <div class="legend" *ngIf="!isLoading">
    <div class="legend-item">
      <span class="swatch approved"></span>
      <span>Approved Hours</span>
    </div>

    <div class="legend-item">
      <span class="swatch match"></span>
      <span>Clocked = Booked</span>
    </div>

    <div class="legend-item">
      <span class="swatch over-booked"></span>
      <span>Clocked &gt; Booked</span>
    </div>

    <div class="legend-item">
      <span class="swatch under-booked"></span>
      <span>Clocked &lt; Booked</span>
    </div>

    <div class="legend-item">
      <span class="swatch incomplete"></span>
      <span>Missing Punch</span>
    </div>

    <div class="legend-item">
      <span class="swatch paycode-holiday"></span>
      <span>Holiday</span>
    </div>

    <div class="legend-item">
      <span class="swatch requested-offtime"></span>
      <span>Requested Offtime</span>
    </div>
  </div>

  <div class="week-nav" *ngIf="!isLoading">
    <button class="nav-btn" type="button" (click)="prevWeek()">← Previous</button>

    <h2 class="week-label">
      Week {{ currentWeek.yyyyww }}
      <span class="dates">
        ({{ currentWeek.startDate.format('MMM D') }} -
        {{ currentWeek.endDate.format('MMM D') }})
      </span>
    </h2>

    <div class="nav-actions">
      <button class="nav-btn" type="button" (click)="nextWeek()">Next →</button>
    </div>
  </div>

  <div class="export-container" *ngIf="!isLoading && !weeksIsLoading">
    <div class="export-range">
      <label>
        Start
        <select [(ngModel)]="exportStartWeek" (ngModelChange)="onExportStartChange($event)">
          <option [ngValue]="null" disabled>Select week</option>
          <option *ngFor="let week of weeks" [ngValue]="week.fullYearWeek">
            {{ week.fullYearWeek }} ({{ week.startDate }} - {{ week.endDate }})
          </option>
        </select>
      </label>

      <label>
        End
        <select [(ngModel)]="exportEndWeek" (ngModelChange)="onExportEndChange($event)">
          <option [ngValue]="null" disabled>Select week</option>
          <option *ngFor="let week of weeks" [ngValue]="week.fullYearWeek">
            {{ week.fullYearWeek }} ({{ week.startDate }} - {{ week.endDate }})
          </option>
        </select>
      </label>

      <button
        class="nav-btn export-btn"
        type="button"
        [disabled]="!exportStartWeek || !exportEndWeek"
        (click)="exportRangeToExcel($event)"
      >
        Export
      </button>
    </div>

    <div class="export-status error" *ngIf="weeksError">{{ weeksError }}</div>
  </div>

  <div class="filters-container" *ngIf="!isLoading">
    <div class="filters-row">
      <label class="filter-field">
        <span>Company</span>
        <select [(ngModel)]="selectedCompanyId" (ngModelChange)="onCompanyChange($event)">
          <option [ngValue]="null">All companies</option>
          <option
            *ngFor="let company of filterService.filterValues?.companies"
            [ngValue]="company.id"
          >
            {{ company.name }}
          </option>
        </select>
      </label>

      <label class="filter-field">
        <span>Department</span>
        <select [(ngModel)]="selectedDepartmentId" (ngModelChange)="onDepartmentChange($event)">
          <option [ngValue]="null">All departments</option>
          <option
            *ngFor="let department of filterService.filterValues?.departments"
            [ngValue]="department.id"
          >
            {{ department.code }} - {{ department.name }}
          </option>
        </select>
      </label>

      <label class="filter-field search-field">
        <span>Search</span>
        <input
          type="text"
          [(ngModel)]="filters.searchTerm"
          (ngModelChange)="onSearchTermChange($event)"
          placeholder="Search employee name"
        />
      </label>

      <div class="filter-actions">
        <button
          class="nav-btn clear-filters-btn"
          type="button"
          (click)="clearFilters()"
          [disabled]="!hasActiveFilters"
        >
          Clear Filters
        </button>
      </div>
    </div>
  </div>

  @if (isLoading || weeksIsLoading) {
    <div class="loading-overlay">
      <div class="spinner"></div>
    </div>
  }

  @if (errorMessage) {
    <div class="error-message">
      {{ errorMessage }}
    </div>
  }

  <ng-container *ngIf="timesheets$ | async as timesheets">
    <div *ngIf="timesheets.length; else noEmployees" class="employee-card">
      <div class="timesheet-table-wrapper">
        <table class="timesheet-table">
          <thead>
            <tr>
              <th class="employee-col">Employee</th>

              @for (dayIndex of [0, 1, 2, 3, 4, 5, 6]; track dayIndex) {
                <th>{{ getDayLabel(dayIndex) }}</th>
              }

              <th class="total-header">
                <div class="total-header-wrap">
                  <span>Total</span>
                  <small>Booked / Scheduled</small>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr *ngFor="let ts of timesheets">
              <td class="employee-col clickable-cell" (click)="openAdminTimesheet(ts.user.id)">
                {{ ts.user.lastName }}, {{ ts.user.firstName }}
                <span class="employee-number">
                  ({{ ts.user.employeeNumber || ts.user.id }})
                </span>
              </td>

              <td
                *ngFor="let dayIndex of [0, 1, 2, 3, 4, 5, 6]"
                class="hour-cell clickable-cell"
                [ngClass]="ts.dailyClasses[dayIndex]"
                (click)="openAdminTimesheet(ts.user.id)"
              >
                <span class="hour-value">
                  {{ ts.displayHours[dayIndex].number | number: '1.2-2' }}
                </span>

                <div
                  class="punch-tooltip"
                  *ngIf="ts.formattedTimeLogs[dayIndex] || ts.bookedTooltips[dayIndex]"
                >
                  <div *ngIf="ts.formattedTimeLogs[dayIndex]">
                    {{ ts.formattedTimeLogs[dayIndex] }}
                  </div>

                  <div *ngIf="ts.bookedTooltips[dayIndex]">
                    {{ ts.bookedTooltips[dayIndex] }}
                  </div>
                </div>
              </td>

              <td
                class="total-col clickable-cell"
                [ngClass]="ts.totalClass"
                (click)="openAdminTimesheet(ts.user.id)"
              >
                <div class="total-col-wrap schedule-total">
                  <span class="total-value">{{ ts.bookedVsScheduledLabel }}</span>
                </div>
              </td>
            </tr>
          </tbody>

          <tfoot>
            <tr class="summary-row">
              <td class="label-cell">Daily Total Booked</td>

              @for (dayIndex of [0, 1, 2, 3, 4, 5, 6]; track dayIndex) {
                <td>{{ dailyTotals[dayIndex] | number: '1.2-2' }}</td>
              }

              <td class="total-col">
                {{ weeklyTotal | number: '1.2-2' }}
              </td>
            </tr>

            <tr class="summary-row average-row">
              <td class="label-cell">Daily Avg Booked ({{ timesheets.length }})</td>

              @for (dayIndex of [0, 1, 2, 3, 4, 5, 6]; track dayIndex) {
                <td>{{ dailyAverages[dayIndex] | number: '1.2-2' }}</td>
              }

              <td class="total-col">
                {{ overallAverage | number: '1.2-2' }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <ng-template #noEmployees>
      <div class="no-data">No employees found for this week.</div>
    </ng-template>
  </ng-container>
</div>